import { useEffect, useState } from "react";

import * as Sentry from "@sentry/react";

import { getAuthToken } from "app/utils/auth";
import { listenToAuth } from "app/utils/authHub";

/**
 * Resolves the Cognito access token and keeps it fresh by subscribing to
 * Amplify's `Hub` `auth` channel. On `tokenRefresh` the JWT is re-fetched
 * and propagated downstream — `useJun0Auth` re-POSTs `/auth/header` to
 * mint a new access_token cookie, which causes `useChat` to reconnect the
 * socket. The user keeps a live chat across the ~hourly Cognito rotation
 * with no page reload. On `signedOut`, drops the token to null so consumers
 * see the unauthorized state.
 *
 * Pattern reference: `apps/console/src/bootstrap.ts` (the same Hub channel
 * powers app-wide redirect handling).
 */
export function useAuthToken(): string | null {
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      try {
        const t = await getAuthToken();
        if (!cancelled) setToken(t);
      } catch (err) {
        if (cancelled) return;
        Sentry.captureException(err, {
          tags: { feature: "jun0", step: "fetch-cognito-token" },
        });
      }
    }

    refresh();

    const unsubscribe = listenToAuth(({ payload }) => {
      if (payload.event === "tokenRefresh") {
        refresh();
      } else if (payload.event === "signedOut") {
        if (!cancelled) setToken(null);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return token;
}
